/**
 * Base API Service
 *
 * Provides authenticated HTTP client for customer API requests.
 * Uses AWS Amplify to fetch auth tokens automatically.
 */

import { fetchAuthSession } from 'aws-amplify/auth'

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1/customer'

/**
 * API Error class with status code and details
 */
export class ApiError extends Error {
  public readonly statusCode: number
  public readonly errorCode?: string
  public readonly details?: unknown

  constructor(
    message: string,
    statusCode: number,
    options?: { errorCode?: string; details?: unknown }
  ) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.errorCode = options?.errorCode
    this.details = options?.details
  }

  /**
   * Check if error is authentication related
   */
  isAuthError(): boolean {
    return this.statusCode === 401 || this.statusCode === 403
  }

  /**
   * Check if error is a not found error
   */
  isNotFound(): boolean {
    return this.statusCode === 404
  }
}

/**
 * Standard API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
  }
}

/**
 * Request options
 */
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
  skipAuth?: boolean
}

/**
 * Get current ID token from Amplify session
 */
async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    // Use idToken instead of accessToken because the backend needs
    // custom Cognito attributes (e.g., custom:consultant_id) which
    // are only included in the idToken, not the accessToken
    const token = session.tokens?.idToken?.toString()
    return token || null
  } catch {
    return null
  }
}

/**
 * Make an authenticated API request
 *
 * @param endpoint - API endpoint (e.g., '/orders')
 * @param options - Request options
 * @returns Parsed response data
 * @throws ApiError on failure
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options

  // Build request headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  }

  // Add auth token if required
  if (!skipAuth) {
    const token = await getIdToken()
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`
    }
  }

  // Build full URL
  const url = `${API_BASE_URL}${endpoint}`

  // Make the request
  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
  })

  // Parse response
  let responseData: ApiResponse<T> | T

  try {
    responseData = await response.json()
  } catch {
    // Response is not JSON
    if (!response.ok) {
      throw new ApiError(`Request failed with status ${response.status}`, response.status)
    }
    // Return empty object for successful non-JSON responses
    return {} as T
  }

  // Check for error response
  if (!response.ok) {
    const apiResponse = responseData as ApiResponse<T>
    throw new ApiError(
      apiResponse.error?.message || `Request failed with status ${response.status}`,
      response.status,
      {
        errorCode: apiResponse.error?.code,
        details: responseData,
      }
    )
  }

  // Handle wrapped response format
  const apiResponse = responseData as ApiResponse<T>
  if ('success' in apiResponse && apiResponse.success && apiResponse.data !== undefined) {
    return apiResponse.data
  }

  // Return raw response if not wrapped
  return responseData as T
}

/**
 * Convenience method for GET requests
 */
export function get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' })
}

/**
 * Convenience method for POST requests
 */
export function post<T>(
  endpoint: string,
  body: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'POST', body })
}

/**
 * Convenience method for PUT requests
 */
export function put<T>(
  endpoint: string,
  body: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PUT', body })
}

/**
 * Convenience method for DELETE requests
 */
export function del<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' })
}

// Export named API service
export const apiService = {
  request: apiRequest,
  get,
  post,
  put,
  delete: del,
  getIdToken,
}

export default apiService
